import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Target, Sparkles, Mail, Users, ListChecks, Lightbulb, Lock, Loader2, ChevronRight,
  Globe, TrendingUp, ExternalLink,
} from 'lucide-react';
import {
  preciseApplicationPathService,
  MatchedJobPreview,
  ApplicationPath,
} from '../services/preciseApplicationPathService';

const PreciseApplicationPath: React.FC = () => {
  const [jobs, setJobs] = useState<MatchedJobPreview[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [upgradeRequired, setUpgradeRequired] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [paths, setPaths] = useState<ApplicationPath[]>([]);
  const [selectedJob, setSelectedJob] = useState<string | 'all'>('all');

  useEffect(() => {
    (async () => {
      try {
        const data = await preciseApplicationPathService.getMatchedJobs();
        setJobs(data);
      } catch (err: any) {
        if (err?.response?.status === 403 && err?.response?.data?.upgradeRequired) {
          setUpgradeRequired(true);
        } else {
          toast.error('Could not load your matched jobs.');
        }
      } finally {
        setLoadingJobs(false);
      }
    })();
  }, []);

  const handleGenerate = async () => {
    setGenerating(true);
    setPaths([]);
    try {
      const result = await preciseApplicationPathService.generate(
        selectedJob === 'all' ? undefined : selectedJob
      );
      if (result.length === 0) {
        toast.error('No matching jobs to build a path for yet.');
      } else {
        setPaths(result);
      }
    } catch (err: any) {
      if (err?.response?.status === 403 && err?.response?.data?.upgradeRequired) {
        setUpgradeRequired(true);
      } else {
        toast.error(err?.response?.data?.message || 'Failed to generate your application path.');
      }
    } finally {
      setGenerating(false);
    }
  };

  if (upgradeRequired) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 mb-6">
          <Lock className="w-8 h-8 text-indigo-600" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Precise Application Path is a premium feature</h1>
        <p className="mt-3 text-gray-600">
          Upgrade to Student Plus or Pro to unlock AI-crafted, personalised game plans for landing your
          top-matched jobs — including who to contact, email drafts, and a multi-step outreach strategy.
        </p>
        <Link
          to="/subscription"
          className="inline-block mt-8 px-6 py-3 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700"
        >
          View plans
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-2">
        <Target className="w-7 h-7 text-indigo-600" />
        <h1 className="text-2xl font-bold text-gray-900">Precise Application Path</h1>
        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-700">Premium</span>
      </div>
      <p className="text-gray-600 mb-8">
        Pick a job and get a bespoke plan to stand out: how to position your skills, who to reach out to,
        likely work-email patterns, ready-to-send drafts, and a step-by-step outreach sequence.
      </p>

      {/* Controls */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-8">
        {loadingJobs ? (
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading your matched jobs…
          </div>
        ) : (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-2">Target job</label>
            <select
              value={selectedJob}
              onChange={(e) => setSelectedJob(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 mb-4"
            >
              <option value="all">Top 3 matched jobs</option>
              {jobs.map((j) => (
                <option key={j.jobId} value={j.jobId}>
                  {j.title} — {j.company} ({j.matchScore}% match)
                </option>
              ))}
            </select>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-60"
            >
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {generating ? 'Crafting your plan…' : 'Generate application path'}
            </button>
            {generating && (
              <p className="mt-3 text-sm text-gray-500">
                Our AI is researching the role and building your outreach strategy — this can take up to a minute.
              </p>
            )}
          </>
        )}
      </div>

      {/* Results */}
      <div className="space-y-8">
        {paths.map((path) => (
          <PathCard key={path.jobId} path={path} />
        ))}
      </div>
    </div>
  );
};

const PathCard: React.FC<{ path: ApplicationPath }> = ({ path }) => (
  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
    <div className="p-5 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-white">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-900">{path.jobTitle}</h2>
          <p className="text-gray-600">{path.company}</p>
        </div>
        <div className="flex items-center gap-2">
          {path.researched && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky-100 text-sky-700 text-xs font-semibold">
              <Globe className="w-3.5 h-3.5" /> Researched live
            </span>
          )}
          <span className="px-3 py-1 rounded-full bg-indigo-600 text-white text-sm font-semibold">
            {path.matchScore}% match
          </span>
        </div>
      </div>
      {path.fitSummary && <p className="mt-3 text-gray-700">{path.fitSummary}</p>}
    </div>

    <div className="p-5 space-y-6">
      {path.researchInsights && (
        <Section icon={<TrendingUp className="w-4 h-4" />} title="What we found about this company">
          {path.researchInsights.companyContext && (
            <p className="text-gray-700">{path.researchInsights.companyContext}</p>
          )}
          <div className="mt-3 grid sm:grid-cols-2 gap-4">
            {path.researchInsights.recentSignals.length > 0 && (
              <InsightList label="Recent signals" items={path.researchInsights.recentSignals} />
            )}
            {path.researchInsights.whatTheyValue.length > 0 && (
              <InsightList label="What they value" items={path.researchInsights.whatTheyValue} />
            )}
            {path.researchInsights.industryTrends.length > 0 && (
              <InsightList label="Industry trends" items={path.researchInsights.industryTrends} />
            )}
          </div>
          {path.researchInsights.sources.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {path.researchInsights.sources.map((url, i) => (
                <a
                  key={i}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline"
                >
                  <ExternalLink className="w-3 h-3" /> {hostOf(url)}
                </a>
              ))}
            </div>
          )}
        </Section>
      )}

      {path.positioningAngle && (
        <Section icon={<Lightbulb className="w-4 h-4" />} title="Your positioning angle">
          <p className="text-gray-700">{path.positioningAngle}</p>
          {path.keyStrengthsToLead.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {path.keyStrengthsToLead.map((s, i) => (
                <span key={i} className="px-2.5 py-1 rounded-full bg-green-100 text-green-800 text-xs font-medium">
                  {s}
                </span>
              ))}
            </div>
          )}
          {path.gapsToAddress.length > 0 && (
            <p className="mt-3 text-sm text-gray-500">
              Address these gaps: {path.gapsToAddress.join(', ')}
            </p>
          )}
        </Section>
      )}

      {path.targetContacts.length > 0 && (
        <Section icon={<Users className="w-4 h-4" />} title="Who to reach out to">
          <div className="space-y-3">
            {path.targetContacts.map((c, i) => (
              <div key={i} className="rounded-lg border border-gray-100 p-3">
                <div className="font-semibold text-gray-900">
                  {c.role} <span className="text-gray-400 font-normal">· {c.seniority}</span>
                </div>
                <p className="text-sm text-gray-600 mt-1">{c.whyThisPerson}</p>
                {c.likelyEmailPatterns.length > 0 && (
                  <p className="text-sm mt-2">
                    <span className="text-gray-500">Likely email (best guess): </span>
                    {c.likelyEmailPatterns.map((p, k) => (
                      <code key={k} className="mr-2 px-1.5 py-0.5 bg-gray-100 rounded text-gray-800">{p}</code>
                    ))}
                  </p>
                )}
                {c.whereToFind.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">Find them on: {c.whereToFind.join(', ')}</p>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      {path.emailDrafts.length > 0 && (
        <Section icon={<Mail className="w-4 h-4" />} title="Email drafts">
          <div className="space-y-3">
            {path.emailDrafts.map((d, i) => (
              <div key={i} className="rounded-lg border border-gray-100 p-3">
                <div className="text-xs uppercase tracking-wide text-indigo-600 font-semibold">{d.audience}</div>
                <div className="font-medium text-gray-900 mt-1">Subject: {d.subject}</div>
                <pre className="mt-2 whitespace-pre-wrap font-sans text-sm text-gray-700">{d.body}</pre>
              </div>
            ))}
          </div>
        </Section>
      )}

      {path.outreachPlan.length > 0 && (
        <Section icon={<ListChecks className="w-4 h-4" />} title="Multi-step outreach plan">
          <ol className="space-y-3">
            {path.outreachPlan
              .slice()
              .sort((a, b) => a.tier - b.tier)
              .map((step, i) => (
                <li key={i} className="flex gap-3">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold">
                    {step.tier}
                  </div>
                  <div>
                    <div className="font-medium text-gray-900">
                      {step.stage} <span className="text-gray-400">· {step.channel}</span>
                      <span className="ml-2 text-xs text-gray-500">{step.timing}</span>
                    </div>
                    <p className="text-sm text-gray-700">{step.action}</p>
                    {step.reasoning && (
                      <p className="mt-1 text-sm text-sky-700 bg-sky-50 rounded px-2 py-1">
                        <span className="font-semibold">Why:</span> {step.reasoning}
                      </p>
                    )}
                    {step.suggestedMessage && (
                      <p className="mt-1 text-sm text-gray-500 italic">“{step.suggestedMessage}”</p>
                    )}
                  </div>
                </li>
              ))}
          </ol>
        </Section>
      )}

      {path.successTips.length > 0 && (
        <Section icon={<ChevronRight className="w-4 h-4" />} title="Tips to land it">
          <ul className="list-disc list-inside text-gray-700 space-y-1">
            {path.successTips.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </Section>
      )}

      <p className="text-xs text-gray-400">
        Estimated timeline: ~{path.estimatedTimelineDays} days · Generated{' '}
        {new Date(path.generatedAt).toLocaleString()}. Email patterns are best-guesses — always verify before sending.
      </p>
    </div>
  </div>
);

const Section: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode }> = ({
  icon, title, children,
}) => (
  <div>
    <div className="flex items-center gap-2 text-gray-900 font-semibold mb-2">
      <span className="text-indigo-600">{icon}</span>
      {title}
    </div>
    {children}
  </div>
);

const InsightList: React.FC<{ label: string; items: string[] }> = ({ label, items }) => (
  <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">{label}</div>
    <ul className="space-y-1">
      {items.map((it, i) => (
        <li key={i} className="text-sm text-gray-700 flex gap-1.5">
          <span className="text-indigo-400">•</span>
          <span>{it}</span>
        </li>
      ))}
    </ul>
  </div>
);

function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export default PreciseApplicationPath;
